import { useI18n } from '@ecom/i18n';
import { type ReactElement } from 'react';

import { type DailySalesPoint } from '../api';

import { type KpiTrend } from './KpiCard';

import { CountUp } from '@/components/ui/CountUp';
import { SkIcon } from '@/components/ui/SkIcon';
import { cn } from '@/lib/cn';
import { useLiveStatus } from '@/lib/live-status-context';

const NARROW_NBSP = String.fromCharCode(0x202f);
const NBSP = String.fromCharCode(0xa0);

/**
 * « 2 465,499 DT » → « 2 465 » and « ,499 DT »: the design sets the integer part large and the millimes with the unit
 * at half size. The narrow no-break space `fr-TN` groups with is drawn almost invisibly by Manrope at figure size, so
 * it becomes an ordinary no-break space.
 */
export function splitMoney(value: string): { readonly whole: string; readonly rest: string } {
  const clean = value.replaceAll(NARROW_NBSP, NBSP);
  const match = /^(.*?\d)([.,]\d+.*)$/u.exec(clean);
  if (match === null) return { whole: clean, rest: '' };
  return { whole: match[1] ?? clean, rest: match[2] ?? '' };
}

function Money({ value, className }: { value: string | null; className: string }): ReactElement {
  if (value === null)
    return <span aria-hidden="true" className="ecom-skeleton mt-4 block h-9 w-40 rounded-lg" />;
  const { whole, rest } = splitMoney(value);
  return (
    <strong className={className}>
      <span className="sr-only">{value}</span>
      <span aria-hidden="true">
        <CountUp value={whole} />
        <small>{rest}</small>
      </span>
    </strong>
  );
}

function Trend({ trend }: { trend: KpiTrend }): ReactElement {
  return (
    <span className={cn('sk-trend', `is-${trend.direction}`)}>
      {trend.direction !== 'flat' ? (
        <SkIcon name={trend.direction === 'up' ? 'trend' : 'trendDown'} size={14} />
      ) : null}
      {trend.label}
    </span>
  );
}

/** The lead card's trend line: the selected period's real daily revenue, drawn as a soft curve under the figure. */
function Sparkline({ points }: { points: readonly DailySalesPoint[] }): ReactElement | null {
  const max = Math.max(...points.map((point) => point.revenue), 0);
  if (points.length < 2 || max === 0) return null;
  const coords = points.map((point, index) => {
    const x = (index / (points.length - 1)) * 180;
    const y = 38 - (point.revenue / max) * 32;
    return `${x.toFixed(1)} ${y.toFixed(1)}`;
  });
  const line = `M${coords.join(' L')}`;
  return (
    <svg
      className="sk-kpi-spark anim-draw"
      viewBox="0 0 180 42"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path d={`${line} V42 H0Z`} fill="currentColor" opacity="0.08" />
      <path
        d={line}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

export interface KpiGridProps {
  readonly todayRevenue: string | null;
  readonly todayTrend: KpiTrend | undefined;
  readonly todayHint: string | undefined;
  readonly monthRevenue: string | null;
  readonly monthHint: string | undefined;
  /** Orders over the selected period, with one bar per day (the last seven days of it). */
  readonly rangeOrders: number | null;
  readonly rangeHint: string;
  readonly points: readonly DailySalesPoint[];
  readonly activeProducts: number | null;
  readonly lowStock: number;
  readonly outOfStock: number;
}

/**
 * The four headline cards of the Figma design (« Souk 2.0 »), deliberately unlike one another: today's revenue on the
 * dark lead card (live status, comparison with yesterday, the period's trend line), the month's revenue, the period's
 * orders with their daily bars, the active products with the stock to watch. Every figure is real; where the design
 * showed sample data with no source here (a monthly objective, « 286 sur 312 »), the card shows what the data does
 * hold instead of inventing it.
 */
export function KpiGrid(props: KpiGridProps): ReactElement {
  const { t } = useI18n();
  const live = useLiveStatus();
  const bars = props.points.slice(-7);
  const maxBar = Math.max(...bars.map((point) => point.orderCount), 0);

  return (
    <section className="sk-kpi-grid" aria-label={t('merchant.dashboard.figuresLabel')}>
      <article className="sk-kpi sk-kpi--primary anim-rise">
        <div className="sk-kpi-head">
          <span className="sk-kpi-label">{t('merchant.dashboard.kpiTodayRevenue')}</span>
          <span className="sk-live" role="status">
            <i className={live === 'live' ? 'live-dot' : 'is-off'} aria-hidden="true" />
            {t(
              live === 'live'
                ? 'merchant.live.on'
                : live === 'connecting'
                  ? 'merchant.live.connecting'
                  : 'merchant.live.off',
            )}
          </span>
        </div>
        <Money value={props.todayRevenue} className="sk-money" />
        <div className="sk-kpi-bottom">
          {props.todayTrend !== undefined ? <Trend trend={props.todayTrend} /> : null}
          {props.todayHint !== undefined ? <small>{props.todayHint}</small> : null}
        </div>
        <Sparkline points={props.points} />
      </article>

      <article className="sk-kpi anim-rise" style={{ animationDelay: '45ms' }}>
        <span className="sk-kpi-icon is-sage">
          <SkIcon name="trend" />
        </span>
        <span className="sk-kpi-label">{t('merchant.dashboard.kpiMonthRevenue')}</span>
        <Money value={props.monthRevenue} className="sk-kpi-value" />
        {props.monthHint !== undefined ? (
          <span className="sk-kpi-note">{props.monthHint}</span>
        ) : null}
      </article>

      <article className="sk-kpi anim-rise" style={{ animationDelay: '90ms' }}>
        <span className="sk-kpi-icon is-mint">
          <SkIcon name="bag" />
        </span>
        <span className="sk-kpi-label">{t('merchant.dashboard.kpiOrdersRange')}</span>
        <div className="sk-kpi-split">
          {props.rangeOrders === null ? (
            <span aria-hidden="true" className="ecom-skeleton block h-8 w-16 rounded-lg" />
          ) : (
            <strong>
              <CountUp value={String(props.rangeOrders)} />
            </strong>
          )}
        </div>
        {maxBar > 0 ? (
          <div className="sk-mini-bars" aria-hidden="true">
            {bars.map((point, index) => (
              <i
                key={point.date}
                style={{
                  height: `${String(Math.max(6, (point.orderCount / maxBar) * 100))}%`,
                  animationDelay: `${String(120 + index * 40)}ms`,
                }}
              />
            ))}
          </div>
        ) : null}
        <span className="sk-kpi-note">{props.rangeHint}</span>
      </article>

      <article className="sk-kpi anim-rise" style={{ animationDelay: '135ms' }}>
        <span className="sk-kpi-icon is-amber">
          <SkIcon name="box" />
        </span>
        <span className="sk-kpi-label">{t('merchant.dashboard.kpiActiveProducts')}</span>
        <div className="sk-kpi-split">
          {props.activeProducts === null ? (
            <span aria-hidden="true" className="ecom-skeleton block h-8 w-16 rounded-lg" />
          ) : (
            <strong>
              <CountUp value={String(props.activeProducts)} />
            </strong>
          )}
        </div>
        <div className="sk-stock-row">
          <span>
            <i className="sk-fill-amber" aria-hidden="true" />
            {t('merchant.dashboard.stockLow')}{' '}
            <b className="num font-semibold text-ink">{props.lowStock}</b>
          </span>
          <span>
            <i className="sk-fill-red" aria-hidden="true" />
            {t('merchant.dashboard.stockOut')}{' '}
            <b className="num font-semibold text-ink">{props.outOfStock}</b>
          </span>
        </div>
      </article>
    </section>
  );
}
